import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { ProCard } from '@ant-design/pro-components';
import { Alert, App, Button, Flex, Switch, Typography } from 'antd';
import { useIntl } from 'react-intl';

import Permission from '@/components/permission';
import AddVersionModal from '@/pages/content-filter/components/AddVersionModal';
import WhitelistSection from '@/pages/content-filter/components/WhitelistSection';
import {
    contentFilterVersionDelete,
    contentFilterVersionList,
    contentFilterVersionUpdate,
    contentWhitelistCreate,
    contentWhitelistDelete,
    contentWhitelistList,
    contentWhitelistUpdate,
    type ContentFilterPlatform,
    type ContentFilterVersionItem,
    type ContentWhitelistItem,
} from '@/services/content-filter';

const PERMISSION = 'Client-Config_Content-Filter';
const CREATE_PERMISSION = 'Client-Config_Content-Filter_Create';
const DELETE_PERMISSION = 'Client-Config_Content-Filter_Delete';

const PLATFORMS: ContentFilterPlatform[] = ['ios', 'android'];
const BANNER_LIMIT = 5;
const LIST_PAGE_SIZE = 999;

type PageState = {
    versions: ContentFilterVersionItem[];
    dramas: ContentWhitelistItem[];
    loading: boolean;
};

const PageContentFilter: React.FC = () => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();

    const [state, setState] = useState<PageState>({ versions: [], dramas: [], loading: false });
    const [adding, setAdding] = useState(false);
    const [versionModal, setVersionModal] = useState<{ open: boolean; platform: ContentFilterPlatform }>({
        open: false,
        platform: 'ios',
    });
    const [switchingIds, setSwitchingIds] = useState<string[]>([]);
    const fetchSeqRef = useRef(0);

    const fetchAll = useCallback(async () => {
        const seq = ++fetchSeqRef.current;
        setState((prev) => ({ ...prev, loading: true }));
        try {
            const [versionsRes, dramaRes] = await Promise.all([
                contentFilterVersionList({ page: 1, size: LIST_PAGE_SIZE }),
                contentWhitelistList({ page: 1, size: LIST_PAGE_SIZE, target_type: 'drama' }),
            ]);

            if (seq !== fetchSeqRef.current) {
                return;
            }

            if (versionsRes.data.code !== 200 || dramaRes.data.code !== 200) {
                message.error(
                    versionsRes.data.msg || dramaRes.data.msg ||
                        formatMessage({ id: 'contentFilter.message.loadFailed' }),
                );
                return;
            }

            setState({
                dramas: dramaRes.data.data ?? [],
                loading: false,
                versions: versionsRes.data.data ?? [],
            });
        } catch (error: any) {
            if (seq === fetchSeqRef.current) {
                message.error(
                    error?.response?.data?.msg ||
                        error?.message ||
                        formatMessage({ id: 'contentFilter.message.loadFailed' }),
                );
                setState((prev) => ({ ...prev, loading: false }));
            }
        }
    }, [formatMessage, message]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const enabledVersionCount = useMemo(
        () => state.versions.filter((version) => version.enabled).length,
        [state.versions],
    );

    const bannerCount = useMemo(() => state.dramas.filter((item) => item.banner).length, [state.dramas]);

    const whitelistEmpty = state.dramas.length === 0;

    const handleToggleVersion = useCallback(
        async (version: ContentFilterVersionItem, enabled: boolean) => {
            setSwitchingIds((prev) => [...prev, version.id]);
            try {
                const response = await contentFilterVersionUpdate(version.id, { enabled });
                const { data } = response;
                if (data.code !== 200) {
                    message.error(data.msg || formatMessage({ id: 'contentFilter.message.saveFailed' }));
                    return;
                }
                setState((prev) => ({
                    ...prev,
                    versions: prev.versions.map((item) => (item.id === version.id ? { ...item, enabled } : item)),
                }));
            } catch (error: any) {
                message.error(
                    error?.response?.data?.msg ||
                        error?.message ||
                        formatMessage({ id: 'contentFilter.message.saveFailed' }),
                );
            } finally {
                setSwitchingIds((prev) => prev.filter((id) => id !== version.id));
            }
        },
        [formatMessage, message],
    );

    const handleRemoveVersion = useCallback(
        async (version: ContentFilterVersionItem) => {
            try {
                const response = await contentFilterVersionDelete(version.id);
                const { data } = response;
                if (data.code !== 200) {
                    message.error(data.msg || formatMessage({ id: 'contentFilter.message.saveFailed' }));
                    return;
                }
                message.success(formatMessage({ id: 'contentFilter.message.versionRemoved' }));
                setState((prev) => ({
                    ...prev,
                    versions: prev.versions.filter((item) => item.id !== version.id),
                }));
            } catch (error: any) {
                message.error(
                    error?.response?.data?.msg ||
                        error?.message ||
                        formatMessage({ id: 'contentFilter.message.saveFailed' }),
                );
            }
        },
        [formatMessage, message],
    );

    const handleAddWhitelist = useCallback(
        async (targetIds: string[]): Promise<boolean> => {
            if (targetIds.length === 0) {
                message.warning(formatMessage({ id: 'contentFilter.message.noIdsWarning' }));
                return false;
            }

            setAdding(true);
            try {
                const response = await contentWhitelistCreate({ target_ids: targetIds, target_type: 'drama' });
                const { data } = response;

                if (data.code !== 200) {
                    message.error(data.msg || formatMessage({ id: 'contentFilter.message.saveFailed' }));
                    return false;
                }

                const result = data.data ?? { created: 0, invalid: [] };

                message.success(
                    formatMessage({ id: 'contentFilter.message.createSuccess' }, { count: result.created }),
                );

                if (result.invalid.length > 0) {
                    message.warning(
                        formatMessage(
                            { id: 'contentFilter.message.invalidSkipped' },
                            { ids: result.invalid.join(', ') },
                        ),
                    );
                }

                fetchAll();
                return true;
            } catch (error: any) {
                message.error(
                    error?.response?.data?.msg ||
                        error?.message ||
                        formatMessage({ id: 'contentFilter.message.saveFailed' }),
                );
                return false;
            } finally {
                setAdding(false);
            }
        },
        [fetchAll, formatMessage, message],
    );

    const handleToggleBanner = useCallback(
        async (item: ContentWhitelistItem, banner: boolean) => {
            try {
                const response = await contentWhitelistUpdate(item.id, { banner });
                const { data } = response;
                if (data.code !== 200) {
                    message.error(data.msg || formatMessage({ id: 'contentFilter.message.saveFailed' }));
                    return;
                }
                setState((prev) => ({
                    ...prev,
                    dramas: prev.dramas.map((drama) => (drama.id === item.id ? { ...drama, banner } : drama)),
                }));
            } catch (error: any) {
                message.error(
                    error?.response?.data?.msg ||
                        error?.message ||
                        formatMessage({ id: 'contentFilter.message.saveFailed' }),
                );
            }
        },
        [formatMessage, message],
    );

    const handleRemoveWhitelist = useCallback(
        async (item: ContentWhitelistItem) => {
            try {
                const response = await contentWhitelistDelete(item.id);
                const { data } = response;
                if (data.code !== 200) {
                    message.error(data.msg || formatMessage({ id: 'contentFilter.message.saveFailed' }));
                    return;
                }
                message.success(formatMessage({ id: 'contentFilter.message.removeSuccess' }));
                fetchAll();
            } catch (error: any) {
                message.error(
                    error?.response?.data?.msg ||
                        error?.message ||
                        formatMessage({ id: 'contentFilter.message.saveFailed' }),
                );
            }
        },
        [fetchAll, formatMessage, message],
    );

    const renderVersionGroup = (platform: ContentFilterPlatform) => {
        const group = state.versions.filter((version) => version.platform === platform);
        const enabled = group.filter((version) => version.enabled).length;

        return (
            <div key={platform}>
                <Flex align="center" gap={12} style={{ marginBottom: 12 }}>
                    <Typography.Text strong>
                        {formatMessage({ id: `contentFilter.versions.platform.${platform}` })}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {formatMessage(
                            { id: 'contentFilter.versions.count' },
                            { total: group.length, enabled, disabled: group.length - enabled },
                        )}
                    </Typography.Text>
                    <Permission permission={CREATE_PERMISSION}>
                        <Button
                            size="small"
                            type="primary"
                            ghost
                            icon={<PlusOutlined />}
                            onClick={() => setVersionModal({ open: true, platform })}
                        >
                            {formatMessage({ id: 'contentFilter.versions.add' })}
                        </Button>
                    </Permission>
                </Flex>
                {group.length === 0 ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
                        {formatMessage({ id: 'contentFilter.versions.empty' })}
                    </Typography.Text>
                ) : (
                    <Flex gap={12} wrap>
                        {group.map((version) => (
                            <Flex
                                key={version.id}
                                align="center"
                                gap={10}
                                style={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                    padding: '6px 10px',
                                    background: '#fafafa',
                                }}
                            >
                                <Typography.Text code strong style={{ fontSize: 13 }}>
                                    {version.builder}
                                </Typography.Text>
                                <Switch
                                    size="small"
                                    checked={version.enabled}
                                    loading={switchingIds.includes(version.id)}
                                    onChange={(checked) => handleToggleVersion(version, checked)}
                                    checkedChildren={formatMessage({ id: 'contentFilter.versions.enabled' })}
                                    unCheckedChildren={formatMessage({ id: 'contentFilter.versions.disabled' })}
                                />
                                <Permission permission={DELETE_PERMISSION}>
                                    <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<CloseOutlined />}
                                        onClick={() => handleRemoveVersion(version)}
                                    />
                                </Permission>
                            </Flex>
                        ))}
                    </Flex>
                )}
            </div>
        );
    };

    return (
        <Permission permission={PERMISSION}>
            <Flex vertical gap={16}>
                {whitelistEmpty && enabledVersionCount > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message={formatMessage(
                            { id: 'contentFilter.emptyWarning' },
                            { count: enabledVersionCount },
                        )}
                    />
                )}
                <ProCard
                    title={formatMessage({ id: 'contentFilter.versions.title' })}
                    headerBordered
                    loading={state.loading}
                >
                    <Flex vertical gap={20}>
                        {PLATFORMS.map((platform) => renderVersionGroup(platform))}
                    </Flex>
                </ProCard>
                <WhitelistSection
                    whitelist={state.dramas}
                    bannerCount={bannerCount}
                    bannerLimit={BANNER_LIMIT}
                    enabledVersionCount={enabledVersionCount}
                    adding={adding}
                    onAdd={handleAddWhitelist}
                    onToggleBanner={handleToggleBanner}
                    onRemove={handleRemoveWhitelist}
                />
            </Flex>

            <AddVersionModal
                open={versionModal.open}
                platform={versionModal.platform}
                onClose={() => setVersionModal((prev) => ({ ...prev, open: false }))}
                onSuccess={fetchAll}
            />
        </Permission>
    );
};

export default PageContentFilter;
